/**
 * Date utility functions to handle timezone issues with date-only strings.
 * 
 * Problem: When date-only strings like "2024-11-24" are parsed with new Date(),
 * JavaScript treats them as UTC midnight. Then toLocaleDateString() converts to
 * local time, which can shift the date back a day in timezones behind UTC.
 * 
 * Solution: Parse date-only strings (YYYY-MM-DD) as local dates instead of UTC dates.
 */

/**
 * Parses a date-only string (YYYY-MM-DD) as a local date, not UTC.
 * This prevents timezone shifts when displaying dates.
 */
export function parseLocalDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  
  // Check if it's a date-only string (YYYY-MM-DD format)
  const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
  
  if (dateOnlyPattern.test(dateString)) {
    // Parse as local date by splitting and using Date constructor with local time
    const [year, month, day] = dateString.split('-').map(Number);
    // Month is 0-indexed in Date constructor
    return new Date(year, month - 1, day);
  }
  
  // For datetime strings or other formats, use standard Date parsing
  return new Date(dateString);
}

/**
 * Formats a date string for display, handling date-only strings correctly.
 */
export function formatDateForDisplay(dateString: string | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!dateString) return '';
  
  const date = parseLocalDate(dateString);
  if (!date) return '';
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  
  return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
}

/**
 * Converts a Date object or date string to YYYY-MM-DD format using local time components.
 * This prevents timezone shifts when converting dates for input fields.
 */
export function formatDateOnly(date: Date | string | null | undefined): string {
  if (!date) return '';
  
  const dateObj = date instanceof Date ? date : parseLocalDate(date);
  if (!dateObj) return '';
  
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}
